import React, { useState, useCallback, useMemo } from 'react';
import { Box, Stack, Typography, Collapse, IconButton, Switch, Chip, useTheme, alpha } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import { useAppSelector } from 'hooks/redux.hooks';
import { selectTranslations } from 'state/reducers/translation.reducers';
import { selectPaymentCategoryTranslation } from 'state/reducers/payment-categories.reducer';
import { IPaymentCategoryNodeProps, MAX_CATEGORY_DEPTH } from 'constants/payment-categories.constants';

// Translation prefix for all keys in this feature
const T_PREFIX = 'page.payment_categories';

const INDENT_SIZE = 24;

/**
 * Action buttons component for category nodes (Edit, Add)
 */
interface IActionButtonsProps {
	isHovered: boolean;
	canAddChild: boolean;
	onEdit: (e: React.MouseEvent) => void;
	onAdd: (e: React.MouseEvent) => void;
	t: (key: string) => string;
}

const ActionButtons: React.FC<IActionButtonsProps> = React.memo(({ isHovered, canAddChild, onEdit, onAdd, t }) => {
	const theme = useTheme();

	return (
		<Stack
			direction="row"
			spacing={0.5}
			sx={{
				opacity: isHovered ? 1 : 0,
				transition: 'opacity 0.2s ease',
			}}
		>
			<IconButton
				size="small"
				onClick={onEdit}
				title={t('button.edit')}
				sx={{
					color: theme.palette.primary.main,
					'&:hover': {
						backgroundColor: alpha(theme.palette.primary.main, 0.08),
					},
				}}
			>
				<EditIcon fontSize="small" />
			</IconButton>

			{canAddChild && (
				<IconButton
					size="small"
					onClick={onAdd}
					title={t('button.add')}
					sx={{
						color: theme.palette.primary.main,
						'&:hover': {
							backgroundColor: alpha(theme.palette.primary.main, 0.08),
						},
					}}
				>
					<AddIcon fontSize="small" />
				</IconButton>
			)}
		</Stack>
	);
});

ActionButtons.displayName = 'ActionButtons';

const PaymentCategoryNodeComponent: React.FC<IPaymentCategoryNodeProps> = ({
	category,
	depth,
	expandedIds,
	onToggleExpand,
	onEdit,
	onAdd,
	onToggleActive,
	pendingStatus,
	getPendingStatus,
	parentIsActive = true,
}) => {
	const theme = useTheme();
	const t = useAppSelector(selectTranslations);
	const categoryTranslation = useAppSelector(selectPaymentCategoryTranslation);
	const [isHovered, setIsHovered] = useState(false);

	const hasChildren = category.children && category.children.length > 0;
	const isExpanded = expandedIds[category.id] ?? false;
	// Can add child if: not at max depth AND category allows children (default true)
	const canAddChild = depth < MAX_CATEGORY_DEPTH && category.allowChildren !== false;
	const handleToggleExpand = useCallback(() => {
		if (hasChildren) {
			onToggleExpand(category.id);
		}
	}, [hasChildren, onToggleExpand, category.id]);

	const handleEdit = useCallback(
		(e: React.MouseEvent) => {
			e.stopPropagation();
			onEdit(category);
		},
		[onEdit, category]
	);

	const handleAdd = useCallback(
		(e: React.MouseEvent) => {
			e.stopPropagation();
			onAdd(category.id);
		},
		[onAdd, category.id]
	);

	const handleToggleActive = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			e.stopPropagation();
			if (onToggleActive) {
				onToggleActive(category);
			}
		},
		[onToggleActive, category]
	);

	// Category is effectively active only if both itself and parent are active
	const isSelfActive = category.isActive !== false;
	const isEffectivelyActive = isSelfActive && parentIsActive;

	// Get hierarchy styling based on depth level - Modern MUI palette
	const hierarchyStyles = useMemo(() => {
		const styles: Record<
			number,
			{
				fontSize: string;
				fontWeight: number;
				backgroundColor: string;
				borderLeft: string;
				paddingY: number;
				marginBottom: number;
				textColor: string;
			}
		> = {
			0: {
				fontSize: '0.95rem',
				fontWeight: 600,
				backgroundColor: theme.palette.grey[50],
				borderLeft: `4px solid ${theme.palette.primary.main}`,
				paddingY: 1.5,
				marginBottom: 0.75,
				textColor: theme.palette.text.primary,
			},
			1: {
				fontSize: '0.9rem',
				fontWeight: 500,
				backgroundColor: theme.palette.grey[100],
				borderLeft: `3px solid ${theme.palette.grey[400]}`,
				paddingY: 1.25,
				marginBottom: 0.5,
				textColor: theme.palette.text.primary,
			},
			2: {
				fontSize: '0.875rem',
				fontWeight: 400,
				backgroundColor: theme.palette.background.paper,
				borderLeft: `2px dashed ${theme.palette.grey[300]}`,
				paddingY: 1,
				marginBottom: 0.25,
				textColor: theme.palette.text.secondary,
			},
		};
		return styles[depth] || styles[2];
	}, [depth, theme]);

	// Get status indicator styling based on pending status
	const statusConfig = useMemo(() => {
		switch (pendingStatus) {
			case 'new':
				return {
					color: theme.palette.success.main,
					backgroundColor: theme.palette.success.light,
					label: t(`${T_PREFIX}.status.new`),
					borderColor: theme.palette.success.main,
				};
			case 'modified':
				return {
					color: theme.palette.info.main,
					backgroundColor: theme.palette.info.light,
					label: t(`${T_PREFIX}.status.modified`),
					borderColor: theme.palette.info.main,
				};
			default:
				return null;
		}
	}, [pendingStatus, theme, t]);

	return (
		<Box
			sx={{
				// Apply muted styling when parent is inactive
				opacity: parentIsActive ? 1 : 0.5,
				pointerEvents: parentIsActive ? 'auto' : 'none',
			}}
		>
			<Stack
				direction="row"
				alignItems="center"
				sx={{
					paddingLeft: `${depth * INDENT_SIZE + 12}px`,
					paddingY: hierarchyStyles.paddingY,
					paddingRight: 1,
					marginBottom: hierarchyStyles.marginBottom,
					borderRadius: '8px',
					cursor: hasChildren ? 'pointer' : 'default',
					transition: 'all 0.2s ease',
					// Use pending status border if available, otherwise use hierarchy border
					borderLeft: statusConfig ? `3px solid ${statusConfig.borderColor}` : hierarchyStyles.borderLeft,
					// Use pending status background if available, otherwise use hierarchy background
					backgroundColor: statusConfig
						? alpha(statusConfig.backgroundColor, 0.2)
						: hierarchyStyles.backgroundColor,
					'&:hover': {
						backgroundColor: theme.palette.action.hover,
						boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
					},
				}}
				onMouseEnter={() => setIsHovered(true)}
				onMouseLeave={() => setIsHovered(false)}
				onClick={handleToggleExpand}
			>
				{/* Expand/Collapse Icon */}
				<Box sx={{ width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
					{hasChildren ? (
						isExpanded ? (
							<ExpandMoreIcon
								sx={{
									color: theme.palette.text.secondary,
									transition: 'transform 0.2s ease',
								}}
							/>
						) : (
							<ChevronRightIcon
								sx={{
									color: theme.palette.text.secondary,
									transition: 'transform 0.2s ease',
								}}
							/>
						)
					) : (
						<Box sx={{ width: 24 }} />
					)}
				</Box>

				{/* Category Name - Display Hebrew translation if available, fallback to name */}
				<Typography
					variant="body1"
					sx={{
						color: isEffectivelyActive ? hierarchyStyles.textColor : theme.palette.text.disabled,
						flex: 1,
						marginLeft: 1,
						textDecoration: isEffectivelyActive ? 'none' : 'line-through',
						opacity: isEffectivelyActive ? 1 : 0.6,
						fontWeight: hierarchyStyles.fontWeight,
						fontSize: hierarchyStyles.fontSize,
					}}
				>
					{categoryTranslation(category.name)}
				</Typography>

				{/* Pending Status Indicator */}
				{statusConfig && (
					<Chip
						size="small"
						label={statusConfig.label}
						sx={{
							marginRight: 1,
							height: '20px',
							fontSize: '0.7rem',
							backgroundColor: statusConfig.backgroundColor,
							color: statusConfig.color,
							fontWeight: 500,
						}}
					/>
				)}

				{/* Active Toggle Switch */}
				{onToggleActive && (
					<Switch
						size="small"
						checked={isSelfActive}
						onChange={handleToggleActive}
						onClick={(e) => e.stopPropagation()}
						sx={{
							opacity: isHovered ? 1 : 0.7,
							transition: 'opacity 0.2s ease',
							'& .MuiSwitch-switchBase.Mui-checked': {
								color: theme.palette.common.white,
								'& + .MuiSwitch-track': {
									backgroundColor: theme.palette.success.main,
									opacity: 1,
								},
							},
							'& .MuiSwitch-switchBase': {
								color: theme.palette.common.white,
								'& + .MuiSwitch-track': {
									backgroundColor: theme.palette.error.main,
									opacity: 0.7,
								},
							},
							'& .MuiSwitch-thumb': {
								boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
							},
						}}
					/>
				)}

				{/* Action Buttons (visible on hover) */}
				<ActionButtons
					isHovered={isHovered}
					canAddChild={canAddChild}
					onEdit={handleEdit}
					onAdd={handleAdd}
					t={t}
				/>
			</Stack>

			{/* Children */}
			{hasChildren && (
				<Collapse in={isExpanded} unmountOnExit>
					<Box
						sx={{
							position: 'relative',
							marginLeft: `${INDENT_SIZE}px`,
							paddingLeft: 2,
							// Subtle vertical guide line connecting children
							'&::before': {
								content: '""',
								position: 'absolute',
								left: 10,
								top: 0,
								bottom: 12,
								width: '1px',
								backgroundColor: theme.palette.grey[300],
							},
						}}
					>
						{category.children?.map((child) => (
							<PaymentCategoryNode
								key={child.id}
								category={child}
								depth={depth + 1}
								expandedIds={expandedIds}
								onToggleExpand={onToggleExpand}
								onEdit={onEdit}
								onAdd={onAdd}
								onToggleActive={onToggleActive}
								pendingStatus={getPendingStatus ? getPendingStatus(child.id) : undefined}
								getPendingStatus={getPendingStatus}
								parentIsActive={isEffectivelyActive}
							/>
						))}
					</Box>
				</Collapse>
			)}
		</Box>
	);
};

export const PaymentCategoryNode = React.memo(PaymentCategoryNodeComponent);
PaymentCategoryNode.displayName = 'PaymentCategoryNode';
